import { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { toast as sonnerToast } from 'sonner';
import { getVoiceAppHost } from '../../../data/voice/voice-app-host';

import { isVoiceWorkspaceEnabled } from '../../../flags';
import { voiceAssistantMessages } from '../components/voice-assistant.messages';
import { voiceController } from '../controller';
import { useVoiceAssistantStore } from '../store/useVoiceAssistantStore';

/**
 * Visit transcription currently owns the mic — don't start Talk-to-Heidi.
 *
 * `activeSessionIdAtom` is the liveness signal ("recording is happening in
 * this tab RIGHT NOW"), cleared on both pause and stop. Deliberately not
 * `agentRecordingSessionIdAtom`: that one is a *routing override* naming the
 * session the recorder should bind to, and only the agent-driven stop path
 * clears it. Gating on it meant a recording started by voice and then stopped
 * with the session record button left the mic looking busy forever.
 */
const isVisitTranscriptionHoldingMic = (): boolean =>
  getVoiceAppHost().isVisitRecordingActive();

/**
 * Thin React surface over the {@link voiceController} singleton. Components
 * read reactive state from the Zustand store and call the imperative actions
 * returned here.
 */
export function useVoiceAssistant() {
  const intl = useIntl();
  const isOpen = useVoiceAssistantStore((s) => s.isOpen);
  const connection = useVoiceAssistantStore((s) => s.connection);
  const sessionState = useVoiceAssistantStore((s) => s.sessionState);
  const muted = useVoiceAssistantStore((s) => s.muted);
  const assistantSpeaking = useVoiceAssistantStore((s) => s.assistantSpeaking);
  const greetingComplete = useVoiceAssistantStore((s) => s.greetingComplete);
  const transcript = useVoiceAssistantStore((s) => s.transcript);
  const tools = useVoiceAssistantStore((s) => s.tools);
  const timeline = useVoiceAssistantStore((s) => s.timeline);
  const approval = useVoiceAssistantStore((s) => s.approval);
  const dictation = useVoiceAssistantStore((s) => s.dictation);
  const error = useVoiceAssistantStore((s) => s.error);
  const micStream = useVoiceAssistantStore((s) => s.micStream);
  const openStore = useVoiceAssistantStore((s) => s.open);
  const closeStore = useVoiceAssistantStore((s) => s.close);
  const minimizeStore = useVoiceAssistantStore((s) => s.minimize);

  const open = useCallback(
    (options?: { audioSeed?: Promise<ArrayBuffer> }) => {
      if (isVisitTranscriptionHoldingMic()) {
        sonnerToast.message(
          intl.formatMessage(voiceAssistantMessages.stopTranscriptionBeforeStarting)
        );
        return;
      }
      openStore();
      // start() is a no-op while a session is already active (reopen after a
      // minimise), so history is preserved there; when it does start fresh it
      // resets synchronously before the first await, so rehydrate afterwards.
      void voiceController.start(
        options?.audioSeed ? { audioSeed: options.audioSeed } : undefined
      );
      // Best-effort server rehydration so history survives a full close/reopen
      // or reload — no-ops when we already hold a transcript (minimise/reopen)
      // and degrades silently if the endpoint is unavailable (AGE-1615).
      void useVoiceAssistantStore.getState().hydrateFromServer();
      // Workspace on → auto-maximize into Voice window; else dock. Dev demo: ⌥⇧H.
      if (isVoiceWorkspaceEnabled()) {
        useVoiceAssistantStore.getState().openWorkspace();
      }
    },
    [intl, openStore]
  );

  // END: tear down the realtime session + mic, then wipe session state.
  const close = useCallback(() => {
    void voiceController.stop();
    closeStore();
  }, [closeStore]);

  // MINIMISE: keep the session + mic + transcript alive; just hide the surface.
  const minimize = useCallback(() => minimizeStore(), [minimizeStore]);

  const toggleMute = useCallback(() => voiceController.toggleMute(), []);
  const sendText = useCallback((text: string) => voiceController.sendText(text), []);
  const resolveApproval = useCallback(
    (approved: boolean, modifiedInput?: Record<string, unknown>) =>
      voiceController.resolveApproval(approved, modifiedInput),
    []
  );
  const toggleDictation = useCallback(
    (target?: string) => voiceController.toggleDictation(target),
    []
  );

  return {
    isOpen,
    connection,
    sessionState,
    muted,
    assistantSpeaking,
    greetingComplete,
    transcript,
    tools,
    timeline,
    approval,
    dictation,
    error,
    micStream,
    open,
    close,
    minimize,
    toggleMute,
    sendText,
    resolveApproval,
    toggleDictation,
  };
}
